/**
 * /users: workbench user administration, for workbench admins only (the
 * `users:read` permission; the nav entry hides without it). Everyone who has
 * signed in, searchable by name or email and filtered by status, all in the URL
 * (`?q=`, `?status=active|deactivated`, `?page=`); a row opens that person in a
 * side panel (`?user=<id>`). Admins manage accounts here, never project data.
 */

import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page";
import { Pill } from "@/components/ui/pill";
import { EmptyState, ErrorState, Skeleton } from "@/components/ui/states";
import { useCurrentUser } from "@/features/auth/hooks";
import { initialsOf } from "@/features/auth/permissions";
import { formatCount, formatRelative, plural } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { PAGE_SIZE, useUserAdminAccess, useUsers } from "./hooks";
import type { UserRow, UserStatus } from "./types";
import { UserPanel } from "./user-panel";
import { StatusLabel } from "./user-status";

const STATUSES: Array<{ value: UserStatus | ""; label: string }> = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "deactivated", label: "Deactivated" },
];

const asStatus = (raw: string | null): UserStatus | "" =>
  raw === "active" || raw === "deactivated" ? raw : "";

/** The search box keeps its own text and writes it to the URL after a pause, so typing is not a request per key. */
function SearchBox({ value, onChange }: { value: string; onChange: (q: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChange(text), 250);
    return () => clearTimeout(t);
  }, [text, value, onChange]);
  return (
    <div className="relative w-full sm:w-72">
      <Search
        size={14}
        strokeWidth={2}
        className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-dim"
        aria-hidden
      />
      <input
        className="inp w-full pl-8"
        type="search"
        placeholder="Search name or email"
        aria-label="Search people"
        maxLength={320}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    </div>
  );
}

function Person({ user, me, onOpen }: { user: UserRow; me: boolean; onOpen: () => void }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden
        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-avatar text-[11px] font-semibold"
      >
        {initialsOf(user.name, user.email)}
      </span>
      <span className="flex min-w-0 flex-col">
        {/* The row is clickable; this button is how a keyboard opens it. */}
        <button
          type="button"
          onClick={(e) => (e.stopPropagation(), onOpen())}
          className="truncate text-left font-medium hover:underline focus-visible:underline"
        >
          {user.name || user.email}
          {me && <span className="font-normal text-muted"> (you)</span>}
        </button>
        {user.name && <span className="truncate text-[12px] text-muted">{user.email}</span>}
        {/* Phones show only this column, so role and status ride along here. */}
        <span className="mt-1 flex items-center gap-2 text-[12px] sm:hidden">
          {user.is_admin && <Pill tone="gold">Admin</Pill>}
          <StatusLabel user={user} />
        </span>
      </span>
    </div>
  );
}

function UsersTable({
  users,
  selected,
  onOpen,
}: {
  users: UserRow[];
  selected: string | null;
  onOpen: (id: string) => void;
}) {
  const me = useCurrentUser().data?.email;
  return (
    <div className="overflow-x-auto">
      <table className="tbl">
        <thead>
          <tr>
            <th>Person</th>
            <th className="hidden sm:table-cell">Role</th>
            <th className="num hidden md:table-cell">Projects</th>
            <th className="hidden md:table-cell">Last active</th>
            <th className="hidden sm:table-cell">Status</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              data-selected={u.id === selected}
              className="cursor-pointer"
              onClick={() => onOpen(u.id)}
            >
              <td className="max-w-[320px]">
                <Person user={u} me={u.email === me} onOpen={() => onOpen(u.id)} />
              </td>
              <td className="hidden sm:table-cell">
                {u.is_admin ? <Pill tone="gold">Admin</Pill> : <span className="text-muted">User</span>}
              </td>
              <td className="num hidden md:table-cell">{formatCount(u.project_count)}</td>
              <td className="hidden whitespace-nowrap text-muted md:table-cell">
                {u.last_seen_at ? formatRelative(u.last_seen_at) : "Never"}
              </td>
              <td className="hidden whitespace-nowrap sm:table-cell">
                <StatusLabel user={u} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const lastPage = (total: number) => Math.max(1, Math.ceil(total / PAGE_SIZE));

function Pager({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) {
  const last = lastPage(total);
  if (last === 1) return null;
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);
  if (from > total) return null;
  return (
    <nav aria-label="Pages" className="flex items-center justify-end gap-2 text-[13px] text-muted">
      <span>
        {formatCount(from)}–{formatCount(to)} of {formatCount(total)}
      </span>
      <Button size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>
        Previous
      </Button>
      <Button size="sm" onClick={() => onPage(page + 1)} disabled={page >= last}>
        Next
      </Button>
    </nav>
  );
}

export function UsersPage() {
  const { canRead } = useUserAdminAccess();
  useEffect(() => {
    document.title = "Users · Workbench";
  }, []);
  if (!canRead)
    return (
      <EmptyState
        title="Workbench admins only"
        // A plain link: this page can stand in for a project that won't open, and a
        // router "/" would land back on that project. `/` opens your last project.
        action={
          <a className="btn" href="/">
            Go home
          </a>
        }
      >
        Only workbench admins can see and manage people here.
      </EmptyState>
    );
  return <People />;
}

/** The list itself, mounted only for admins so nobody else asks the server. */
function People() {
  const url = useUrlState();
  const q = url.get("q") ?? "";
  const status = asStatus(url.get("status"));
  const page = Math.max(1, Number(url.get("page")) || 1);
  const selected = url.get("user");
  const users = useUsers({ q, status, page });
  const search = useCallback((next: string) => url.set({ q: next, page: null }, { replace: true }), [url]);

  const filtered = q !== "" || status !== "";
  const total = users.data?.total;
  return (
    <div className="flex max-w-[1100px] flex-col gap-[18px]">
      <PageHeader
        title="Users"
        subtitle={
          total === undefined
            ? "Everyone who has signed in to the workbench."
            : filtered
              ? `${plural(total, "person", "people")} match.`
              : `${plural(total, "person", "people")} have signed in to the workbench.`
        }
      />
      <div className="flex flex-wrap items-center gap-2" role="search" aria-label="Filter people">
        <SearchBox value={q} onChange={search} />
        <div className="seg" role="group" aria-label="Status">
          {STATUSES.map((s) => (
            <button
              key={s.label}
              type="button"
              aria-pressed={status === s.value}
              onClick={() => url.set({ status: s.value, page: null })}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <Box>
        {users.isPending ? (
          <Skeleton rows={6} label="Loading people" />
        ) : users.isError ? (
          <div className="flex flex-col items-start gap-3 p-4">
            <ErrorState error={users.error} what="people" />
            <Button size="sm" onClick={() => void users.refetch()}>
              Try again
            </Button>
          </div>
        ) : users.data.items.length === 0 && users.data.total > 0 ? (
          // Past the end (say the last people here were just deactivated): say so, and offer a way back.
          <EmptyState
            title="Nothing on this page"
            action={
              <Button onClick={() => url.set({ page: lastPage(users.data.total) })}>Go to last page</Button>
            }
          >
            The list ends on page {lastPage(users.data.total)}.
          </EmptyState>
        ) : users.data.items.length === 0 ? (
          filtered ? (
            <EmptyState
              title="No one matches"
              action={
                <Button onClick={() => url.set({ q: null, status: null, page: null })}>Clear filters</Button>
              }
            >
              {q ? `Nobody's name or email contains “${q}”` : "Nobody has this status"}
              {q && status ? `, among ${status} people.` : "."}
            </EmptyState>
          ) : (
            <EmptyState title="No one has signed in yet">
              People show up here the first time they sign in with their qure.ai account.
            </EmptyState>
          )
        ) : (
          <UsersTable users={users.data.items} selected={selected} onOpen={(id) => url.set({ user: id })} />
        )}
      </Box>
      {users.data && <Pager page={page} total={users.data.total} onPage={(p) => url.set({ page: p })} />}
      {selected && <UserPanel id={selected} onClose={() => url.set({ user: null })} />}
    </div>
  );
}
